import React from 'react';
import { useIntl } from 'react-intl';
import { Badge } from '@hqo/ui-atoms';
import { Lock } from 'lucide-react';

import { AddOn } from 'store/resource/types';
import { AddOnCard } from 'components/add-on-card';
import { AddOnPartyIcon } from 'components/add-on-party-icon';
import { PartySection } from '../group-by-party';

export interface AddOnCatalogProps {
  partySections: Array<PartySection>;
  isPartyLocked: (partyKey: string) => boolean;
  /* RES-1398. Whether this product is one the booking already carries past its notice window. */
  isKept?: (addOn: AddOn) => boolean;
  quantityOf: (addOnId: string) => number;
  minQuantityOf: (addOnId: string) => number;
  isAtMax: (addOnId: string) => boolean;
  isFixedQuantity: (addOnId: string) => boolean;
  isAwaitingRanges: boolean;
  onIncrease: (addOn: AddOn) => void;
  onDecrease: (addOn: AddOn) => void;
  onRemove: (addOn: AddOn) => void;
  expandedId: string | null;
  onToggle: (addOnId: string) => void;
  bookingStart?: string;
  /* The step's page title is the h1, so its parties are h2; the dialog's title is already an h2. */
  headingLevel: 'h2' | 'h3';
  testIdPrefix: string;
}

/**
 * VIS-2193 — the add-on catalogue as image-led cards, grouped by vendor. Shared by the post-booking
 * step and the my-bookings dialog (behind `show-product-images`), so the two surfaces draw the same
 * thing: each vendor's logo (or initials) and name, then one `AddOnCard` per product.
 *
 * `role="group"` per party, labelled by its heading, so a locked card announces the party it belongs
 * to. Named only when there IS a heading: an unnamed group beats a misleadingly-named one.
 */
export const AddOnCatalog = ({
  partySections,
  isPartyLocked,
  isKept = () => false,
  quantityOf,
  minQuantityOf,
  isAtMax,
  isFixedQuantity,
  isAwaitingRanges,
  onIncrease,
  onDecrease,
  onRemove,
  expandedId,
  onToggle,
  bookingStart,
  headingLevel: Heading,
  testIdPrefix,
}: AddOnCatalogProps): JSX.Element => {
  const intl = useIntl();

  return (
    <div className="flex flex-col gap-6 text-foreground" data-testid={testIdPrefix}>
      {partySections.map(section => {
        const isLocked = isPartyLocked(section.key);
        const sectionKey = section.key || 'unattributed';
        const headingId = `${testIdPrefix}-party-${sectionKey}-name`;
        const lockedId = `${testIdPrefix}-party-${sectionKey}-locked`;

        return (
          <section
            key={section.key}
            role="group"
            /* The lock tag joins the name, so every card in a locked party is announced as unavailable. */
            aria-labelledby={
              section.name ? [headingId, isLocked ? lockedId : null].filter(Boolean).join(' ') : undefined
            }
            className="flex flex-col gap-3"
            data-testid={`${testIdPrefix}-party-${sectionKey}`}
          >
            {/* A nameless party still says when it is locked; it just has no name or mark to show. */}
            {(section.name || isLocked) && (
              <Heading className="m-0 flex items-center gap-2">
                {section.name && (
                  <>
                    <AddOnPartyIcon name={section.name} logoUrl={section.logoUrl} />
                    <span id={headingId} className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                      {section.name}
                    </span>
                  </>
                )}
                {isLocked && (
                  <Badge
                    id={lockedId}
                    variant="outline"
                    className="shrink-0 gap-1 text-muted-foreground"
                    data-testid={`${testIdPrefix}-party-locked-${section.key}`}
                  >
                    <Lock className="size-3" aria-hidden="true" />
                    {intl.formatMessage({ id: 'add_on_party_locked' })}
                  </Badge>
                )}
              </Heading>
            )}
            {section.addOns.map(addOn => (
              <AddOnCard
                key={addOn.id}
                addOn={addOn}
                quantity={quantityOf(addOn.id)}
                onIncrease={onIncrease}
                onDecrease={onDecrease}
                onRemove={onRemove}
                isExpanded={expandedId === addOn.id}
                onToggle={onToggle}
                isPartyLocked={isLocked}
                isKept={isKept(addOn)}
                isFixedQuantity={isFixedQuantity(addOn.id)}
                isAtMax={isAtMax(addOn.id)}
                isAwaitingRanges={isAwaitingRanges}
                minQuantity={minQuantityOf(addOn.id)}
                bookingStart={bookingStart}
              />
            ))}
          </section>
        );
      })}
    </div>
  );
};
